import { makeAutoObservable, runInAction } from "mobx";
import commonStore from "../Store/marco/commonStore";
import handleAPIError, {
  MESSAGE_CODES,
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../services/toast";
import { importJobWorkflowAPI } from "./import-job-api";
import {
  createImportFormData,
  getModuleMeta,
  refreshModuleData,
  resolveModuleFromImportType,
  IMPORT_MODULES,
} from "./import-job-config";

import i18n from "../i18n";
import { dataEntryText, isOldDataEntryFileError } from "./data-entry-excel";

const POLL_INTERVAL_MS = 1000;

export const parseImportDate = (value) => {
  if (!value) return null;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const parsed = new Date(hasTimezone ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const normalizeImportJob = (job = {}, fallback = {}) => {
  const merged = { ...fallback, ...job };
  merged.module = merged.module || resolveModuleFromImportType(merged.importType);
  const meta = getModuleMeta(merged.module);
  return {
    ...merged,
    jobId: merged.jobId || merged.id || "",
    fileName: merged.fileName || fallback.fileName || "Unknown file",
    moduleAbbr: merged.moduleAbbr || meta.abbr,
    status: String(merged.status || "").toLowerCase(),
    progress: toNumber(merged.progress),
    totalRows: toNumber(merged.totalRows),
    processedRows: toNumber(merged.processedRows),
    successRows: toNumber(merged.successRows),
    errorRows: toNumber(merged.errorRows),
    errorCount: toNumber(merged.errorCount, toNumber(merged.errorRows)),
    errors: Array.isArray(merged.errors) ? merged.errors : [],
  };
};

const sortNewestFirst = (items) =>
  [...items].sort((a, b) => {
    const aTime = parseImportDate(a.createdAt)?.getTime() || 0;
    const bTime = parseImportDate(b.createdAt)?.getTime() || 0;
    return bTime - aTime;
  });

class ImportJobStore {
  history = [];
  historyLoading = false;
  initializedUserId = "";
  lastSeenJobId = "";
  activeJob = null;
  selectedJob = null;
  selectedContext = null;
  pendingOpenItem = null;
  modalView = "import";
  modalState = "idle";
  validation = null;
  lastError = "";
  allHistoryOpen = false;
  isCheckingActive = false;
  isExporting = false;

  pollingTimer = null;
  pollingInFlight = false;
  validationSource = null;

  constructor() {
    makeAutoObservable(
      this,
      {
        pollingTimer: false,
        pollingInFlight: false,
        validationSource: false,
      },
      { autoBind: true },
    );
  }

  get storageScope() {
    return commonStore.userId || commonStore.authId || "anonymous";
  }

  get activeStorageKey() {
    return `nxmap.import-job.active.${this.storageScope}`;
  }

  get seenStorageKey() {
    return `nxmap.import-job.last-seen.${this.storageScope}`;
  }

  get isValidating() {
    return this.modalState === "validating";
  }

  get currentModuleHistory() {
    const moduleAbbr = this.selectedContext?.moduleAbbr;
    if (!moduleAbbr) return [];
    return this.history.filter((item) => item.moduleAbbr === moduleAbbr);
  }

  get todayHistory() {
    const today = new Date();
    return this.history.filter((item) => {
      const date = parseImportDate(item.createdAt);
      return (
        date &&
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      );
    });
  }

  get latestFinishedJob() {
    return (
      this.todayHistory.find((item) =>
        ["completed", "failed"].includes(item.status),
      ) || null
    );
  }

  get bellState() {
    if (this.activeJob?.status === "processing") return "processing";
    const latest = this.latestFinishedJob;
    if (!latest) return "empty";
    return this.lastSeenJobId === latest.jobId
      ? "empty"
      : "completed";
  }

  setAllHistoryOpen(value) {
    this.allHistoryOpen = value;
  }

  setExporting(value) {
    this.isExporting = value;
  }

  deactivate() {
    this.stopPolling();
    this.closeValidationSource();
    this.initializedUserId = "";
    this.activeJob = null;
    this.selectedJob = null;
    this.selectedContext = null;
    this.lastSeenJobId = "";
    this.history = [];
  }

  queueHistoryItem(item, reupload = false) {
    this.pendingOpenItem = { item, reupload };
  }

  markNotificationsSeen() {
    const latest = this.latestFinishedJob;
    if (latest) {
      this.lastSeenJobId = latest.jobId;
      localStorage.setItem(this.seenStorageKey, latest.jobId);
    }
  }

  persistActiveJob(job) {
    localStorage.setItem(
      this.activeStorageKey,
      JSON.stringify({
        jobId: job.jobId,
        module: job.module,
        moduleAbbr: job.moduleAbbr,
        importType: job.importType,
        fileName: job.fileName,
        status: job.status,
        createdAt: job.createdAt,
        startedAt: job.startedAt,
      }),
    );
  }

  clearPersistedActiveJob() {
    localStorage.removeItem(this.activeStorageKey);
  }

  readPersistedActiveJob() {
    try {
      const value = localStorage.getItem(this.activeStorageKey);
      return value ? normalizeImportJob(JSON.parse(value)) : null;
    } catch (_error) {
      this.clearPersistedActiveJob();
      return null;
    }
  }

  async initialize() {
    const userId = this.storageScope;
    if (!commonStore.token || this.initializedUserId === userId) return;

    this.initializedUserId = userId;
    this.lastSeenJobId = localStorage.getItem(this.seenStorageKey) || "";
    await this.loadHistory();

    const persistedJob = this.readPersistedActiveJob();
    const historyJob = this.history.find((item) => item.status === "processing");
    const job = persistedJob || historyJob;
    if (job?.jobId) this.startPolling(job);
  }

  async loadHistory() {
    this.historyLoading = true;
    try {
      const response = await importJobWorkflowAPI.getHistory("");
      if (response?.success === false) {
        throw new Error(response.error || "Unable to load import history");
      }
      const items = Array.isArray(response?.data?.items)
        ? response.data.items.map((item) => normalizeImportJob(item))
        : [];
      runInAction(() => {
        this.history = sortNewestFirst(items);
      });
      return items;
    } catch (error) {
      console.error("Import history load failed", error);
      return [];
    } finally {
      runInAction(() => {
        this.historyLoading = false;
      });
    }
  }

  async openModal(context) {
    this.selectedContext = context;
    this.lastError = "";

    const pending = this.pendingOpenItem;
    let detailJob = null;
    this.pendingOpenItem = null;

    if (pending) {
      this.selectedJob = normalizeImportJob(pending.item);
      this.modalView = pending.reupload ? "import" : "detail";
      if (pending.reupload) {
        this.modalState = "idle";
        this.selectedJob = null;
      } else {
        this.modalState = this.getResultState(this.selectedJob);
        detailJob = this.selectedJob;
      }
    } else {
      this.selectedJob = null;
      this.modalView = "import";
      this.modalState = "idle";
    }

    await this.checkActiveJob(context);
    if (detailJob && this.shouldLoadJobDetails(detailJob)) {
      await this.loadJobDetails(detailJob);
    }
  }

  getResultState(job) {
    if (!job) return "idle";
    if (job.status === "processing") return "processing";
    if (job.status === "failed") return "failed";
    if (job.status === "completed" && job.errorRows > 0) {
      return "completed-with-errors";
    }
    if (job.status === "completed") return "success";
    return "error";
  }

  isCurrentJob(job, context = this.selectedContext) {
    return Boolean(
      job &&
        context &&
        job.module === context.module &&
        (job.module === IMPORT_MODULES.DE || job.importType === context.importType),
    );
  }

  async checkActiveJob(context = this.selectedContext) {
    this.isCheckingActive = true;
    try {
      const response = await importJobWorkflowAPI.getActiveJob();
      if (response?.success === false) {
        throw new Error(response.error || "Unable to check active import");
      }

      const data = response?.data;
      if (data?.jobId) {
        const activeJob = normalizeImportJob(data, {
          status: "processing",
          moduleAbbr: getModuleMeta(data.module).abbr,
        });
        this.startPolling(activeJob);

        if (this.modalView === "import") {
          this.modalState = this.isCurrentJob(activeJob, context)
            ? "processing"
            : "locked";
        }
      } else if (this.modalView === "import" && !this.isValidating) {
        this.modalState = "idle";
      }
    } catch (error) {
      runInAction(() => {
        this.lastError = error.message || "Unable to check active import";
        if (this.modalView === "import") this.modalState = "error";
      });
      handleAPIError(error, {
        i18nKey: "Unable to check active imports",
        ns: "ActivityMain",
        fallback: "Unable to check active imports",
        throwError: false,
      });
    } finally {
      runInAction(() => {
        this.isCheckingActive = false;
      });
    }
  }

  showHistory() {
    this.modalView = "history";
  }

  viewJob(item) {
    this.selectedJob = normalizeImportJob(item);
    this.modalView = "detail";
    this.modalState = this.getResultState(this.selectedJob);
    if (this.shouldLoadJobDetails(this.selectedJob)) {
      this.loadJobDetails(this.selectedJob);
    }
  }

  shouldLoadJobDetails(job) {
    return Boolean(
      job?.jobId &&
        job.status !== "processing" &&
        (job.status === "failed" || job.errorRows > 0),
    );
  }

  async loadJobDetails(job) {
    try {
      const response = await importJobWorkflowAPI.getJobStatus(job.jobId);
      if (response?.success === false || !response?.data) return;

      const detailedJob = normalizeImportJob(
        {
          ...response.data,
          status: job.status === "failed" ? "failed" : response.data.status,
        },
        job,
      );
      runInAction(() => {
        if (this.selectedJob?.jobId !== detailedJob.jobId) return;
        this.selectedJob = detailedJob;
        this.modalState = this.getResultState(detailedJob);
      });
    } catch (error) {
      console.error("Import job detail load failed", error);
    }
  }

  showImport() {
    this.selectedJob = null;
    this.modalView = "import";
    if (this.activeJob) {
      this.modalState = this.isCurrentJob(this.activeJob)
        ? "processing"
        : "locked";
    } else {
      this.modalState = "idle";
    }
  }

  resetUpload() {
    this.validation = null;
    this.lastError = "";
    this.selectedJob = null;
    this.modalView = "import";
    this.modalState = this.activeJob
      ? this.isCurrentJob(this.activeJob)
        ? "processing"
        : "locked"
      : "idle";
  }

  notifyValidationMustRemainOpen() {
    showInfoToast({
      i18nKey: "File validation is in progress. Keep this window open.",
      ns: "ActivityMain",
      fallback: "File validation is in progress. Keep this window open.",
    });
  }

  async validateAndSubmit(file, context = this.selectedContext) {
    if (!file || !context) return;

    this.selectedContext = context;
    this.modalView = "import";
    this.modalState = "validating";
    this.lastError = "";
    this.validation = {
      stage: "queued",
      progress: 0,
      fileName: file.name,
      errors: [],
      errorCount: 0,
      summary: null,
      reportUrl: "",
    };

    try {
      const validateResponse = await importJobWorkflowAPI.validate(
        createImportFormData(file, context),
      );
      if (validateResponse?.success === false) {
        throw new Error(
          validateResponse.error || validateResponse.message || "Validation failed",
        );
      }

      const validationData = validateResponse?.data;
      if (!validationData?.validationId || !validationData?.streamUrl) {
        throw new Error("Validation response is missing validationId or streamUrl");
      }

      const validationResult = await this.waitForValidation(
        validationData,
        file.name,
      );

      if (validationResult.status !== "ok") {
        runInAction(() => {
          this.validation = {
            ...this.validation,
            ...validationResult,
            validationId: validationData.validationId,
            reportUrl:
              validationResult.reportUrl ||
              `/api/importjob/${validationData.validationId}/error-report`,
          };
          this.modalState = "validation-error";
        });
        return;
      }

      const submitResponse = await importJobWorkflowAPI.submit(
        createImportFormData(file, context),
      );
      if (submitResponse?.success === false || !submitResponse?.data?.jobId) {
        throw new Error(
          submitResponse?.error || submitResponse?.message || "Import submission failed",
        );
      }

      const job = normalizeImportJob(submitResponse.data, {
        module: context.module,
        moduleAbbr: context.moduleAbbr,
        importType: validationResult.importType || validationData.importType || context.importType,
        fileName: file.name,
        status: "processing",
        progress: 0,
        createdAt: new Date().toISOString(),
      });

      runInAction(() => {
        this.selectedJob = job;
        this.modalState = "processing";
      });
      this.startPolling(job);
    } catch (error) {
      this.closeValidationSource();
      runInAction(() => {
        const message = error.response?.data?.error || error.response?.data?.message || error.message;
        this.lastError = context.module === IMPORT_MODULES.DE && isOldDataEntryFileError(message)
          ? dataEntryText("oldFile", i18n.t.bind(i18n), commonStore.lang)
          : message || "Unable to import file";
        this.modalState = "error";
      });
      if (context.module === IMPORT_MODULES.DE) {
        showErrorToast({ fallback: this.lastError });
      } else {
        handleAPIError(error, {
          i18nKey: "Unable to import file",
          ns: "ActivityMain",
          fallback: error.message || "Unable to import file",
          throwError: false,
        });
      }
    }
  }

  waitForValidation(validationData, fileName) {
    return new Promise((resolve, reject) => {
      const finish = (callback, value) => {
        this.closeValidationSource();
        callback(value);
      };

      this.validationSource = importJobWorkflowAPI.createValidationStream(
        validationData.streamUrl,
        commonStore.token,
        {
          onProgress: (data) => {
            runInAction(() => {
              this.validation = {
                ...this.validation,
                ...data,
                fileName: data.fileName || fileName,
              };
            });
          },
          onDone: (data) => {
            runInAction(() => {
              this.validation = {
                ...this.validation,
                ...data,
                validationId: validationData.validationId,
                reportUrl:
                  data.reportUrl ||
                  `/api/importjob/${validationData.validationId}/error-report`,
              };
              if (data.status !== "ok") {
                this.modalState = "validation-error";
              }
            });
            finish(resolve, data);
          },
          onError: (data) =>
            finish(
              reject,
              new Error(data?.error || data?.message || "Validation expired"),
            ),
          onTransportError: (error) => finish(reject, error),
        },
      );
    });
  }

  closeValidationSource() {
    this.validationSource?.close();
    this.validationSource = null;
  }

  startPolling(job) {
    const normalizedJob = normalizeImportJob(job, {
      createdAt: job.createdAt || job.startedAt || new Date().toISOString(),
    });
    if (!normalizedJob.jobId) return;

    if (
      this.activeJob?.jobId === normalizedJob.jobId &&
      (this.pollingTimer || this.pollingInFlight)
    ) {
      this.activeJob = normalizeImportJob(normalizedJob, this.activeJob);
      return;
    }

    this.stopPolling(false);
    this.activeJob = normalizedJob;
    this.history = sortNewestFirst([
      normalizedJob,
      ...this.history.filter((item) => item.jobId !== normalizedJob.jobId),
    ]);
    this.persistActiveJob(normalizedJob);
    this.pollJob(normalizedJob.jobId);
  }

  scheduleNextPoll(jobId) {
    clearTimeout(this.pollingTimer);
    this.pollingTimer = setTimeout(() => this.pollJob(jobId), POLL_INTERVAL_MS);
  }

  async pollJob(jobId) {
    if (this.pollingInFlight || this.activeJob?.jobId !== jobId) return;
    this.pollingInFlight = true;

    try {
      const response = await importJobWorkflowAPI.getJobStatus(jobId);
      if (this.activeJob?.jobId !== jobId) return;
      if (response?.success === false) {
        await this.handlePollingLookupError(
          response.error || "Job not found",
        );
        return;
      }

      const job = normalizeImportJob(response?.data, this.activeJob);
      runInAction(() => {
        this.activeJob = job;
        this.history = this.history.map((item) =>
          item.jobId === job.jobId ? normalizeImportJob(job, item) : item,
        );
        if (
          !this.isValidating &&
          (this.selectedJob?.jobId === job.jobId || this.isCurrentJob(job))
        ) {
          this.selectedJob = job;
          if (this.modalView === "import") this.modalState = "processing";
        }
      });
      this.persistActiveJob(job);

      if (["completed", "failed"].includes(job.status)) {
        await this.handleJobCompleted(job);
        return;
      }

      this.scheduleNextPoll(jobId);
    } catch (error) {
      const apiError = error?.response?.data;
      if (
        apiError?.success === false &&
        /job not found/i.test(apiError.error || "")
      ) {
        await this.handlePollingLookupError(apiError.error);
        return;
      }
      console.error("Import job polling failed", error);
      runInAction(() => {
        this.lastError = error.message || "Unable to refresh import progress";
      });
      this.scheduleNextPoll(jobId);
    } finally {
      this.pollingInFlight = false;
    }
  }

  async handlePollingLookupError(message) {
    const currentJob = this.activeJob;
    await this.loadHistory();
    const historyJob = this.history.find(
      (item) =>
        item.jobId === currentJob?.jobId &&
        ["completed", "failed"].includes(item.status),
    );

    if (historyJob) {
      await this.handleJobCompleted(
        normalizeImportJob(historyJob, currentJob),
      );
      return;
    }

    this.stopPolling(true);
    this.activeJob = null;
    this.lastError = message;
    if (this.isCurrentJob(currentJob) && this.modalView === "import") {
      this.modalState = "error";
    }
    showInfoToast({
      i18nKey: "The import job could not be found or has expired.",
      ns: "ActivityMain",
      fallback: message || "The import job could not be found or has expired.",
    });
  }

  async handleJobCompleted(job) {
    const isWatchingFinishedJob =
      !this.isValidating &&
      (this.selectedJob?.jobId === job.jobId || this.isCurrentJob(job));
    this.stopPolling();
    this.clearPersistedActiveJob();

    runInAction(() => {
      this.activeJob = null;
      if (isWatchingFinishedJob) this.selectedJob = job;
      if (this.modalView === "import" && isWatchingFinishedJob) {
        this.modalState = this.getResultState(job);
      }
    });

    await this.loadHistory();
    let finalJob = job;
    runInAction(() => {
      const historyJob = this.history.find((item) => item.jobId === job.jobId);
      finalJob = normalizeImportJob({
        ...historyJob,
        ...job,
        reportUrl: historyJob?.reportUrl || job.reportUrl,
        status: historyJob?.status === "failed" ? "failed" : job.status,
      });

      this.history = sortNewestFirst([
        finalJob,
        ...this.history.filter((item) => item.jobId !== job.jobId),
      ]);
      if (isWatchingFinishedJob) {
        this.selectedJob = finalJob;
        this.modalState = this.getResultState(finalJob);
      }
    });
    if (finalJob.status !== "failed") {
      try {
        await refreshModuleData(finalJob.module);
      } catch (error) {
        console.error("Imported module refresh failed", error);
      }
    }

    if (finalJob.status === "failed") {
      showErrorToast({
        i18nKey: "Import failed",
        ns: "ActivityMain",
        fallback: "Import failed",
      });
    } else if (finalJob.errorRows > 0) {
      handleAPIError(null, {
        apiData: {
          success: false,
          messageCode:
            MESSAGE_CODES.EMISSION_INVENTORY_IMPORT_COMPLETE_WITH_ERRORS,
        },
        throwError: false,
      });
    } else {
      showSuccessToast({
        scenarioCode: MESSAGE_CODES.EMISSION_INVENTORY_IMPORT_COMPLETE,
      });
    }
  }

  stopPolling(clearPersisted = false) {
    clearTimeout(this.pollingTimer);
    this.pollingTimer = null;
    this.pollingInFlight = false;
    if (clearPersisted) this.clearPersistedActiveJob();
  }

  async downloadReport(jobOrValidation) {
    const reportUrl =
      jobOrValidation?.reportUrl ||
      (jobOrValidation?.jobId
        ? `/api/importjob/jobs/${jobOrValidation.jobId}/error-report`
        : "");
    if (!reportUrl) return;

    const module =
      jobOrValidation.module || this.selectedContext?.module || "import";
    const identifier =
      jobOrValidation.jobId || jobOrValidation.validationId || "validation";

    try {
      await importJobWorkflowAPI.downloadErrorReport(
        reportUrl,
        `error_report_${module}_${identifier}.pdf`,
      );
    } catch (error) {
      handleAPIError(error, {
        apiData: error.apiData,
        i18nKey: "Unable to download error report",
        ns: "ActivityMain",
        fallback: "Unable to download error report",
        throwError: false,
      });
    }
  }
}

const importJobStore = new ImportJobStore();
export default importJobStore;
